import React, { useState, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  BarChart3,
  Target,
  TrendingUp,
  RefreshCw,
  Download,
  Activity,
  Info
} from 'lucide-react';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { WagonWheel } from './WagonWheel';
import { HistoricalTrends } from './HistoricalTrends';
import { useSessionShots, useRecentShots } from '../hooks/useShots';
import { useLiveShots } from '@/hooks/useLiveShots';
import { ShotData } from '../types';
import { calculateShotComparison } from '../utils/shotComparison';
import { generateSampleShots } from '../data/sampleShots';

interface EnhancedAnalyticsDashboardProps {
  sessionId?: string;
  className?: string;
}

export const EnhancedAnalyticsDashboard: React.FC<EnhancedAnalyticsDashboardProps> = ({
  sessionId,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  // Default to 'all' when using sample data
  const [timePeriod, setTimePeriod] = useState<'session' | 'week' | 'month' | 'all'>('all');
  const [shotTypeFilter, setShotTypeFilter] = useState<string>('all');
  const [qualityFilter, setQualityFilter] = useState<string>('all');
  const [highlightShotType, setHighlightShotType] = useState<string | undefined>();
  
  // Data fetching
  const { data: sessionShots, isLoading: sessionLoading, refetch: refetchSession } = useSessionShots(sessionId);
  const { data: recentShots, isLoading: recentLoading } = useRecentShots(20);
  const { liveShots, isLive } = useLiveShots({ sessionId, maxShots: 100 });
  
  // Combine all data sources
  const allShots = useMemo(() => {
    const shots: ShotData[] = [];
    
    // Add session shots if available
    if (sessionShots && sessionShots.length > 0) {
      console.log('Using session shots:', sessionShots.length);
      shots.push(...sessionShots);
    }
    
    // Add recent shots if no session
    if (!sessionId && recentShots && recentShots.length > 0) {
      console.log('Using recent shots:', recentShots.length);
      shots.push(...recentShots);
    }
    
    // Add live shots
    if (liveShots.length > 0) {
      console.log('Using live shots:', liveShots.length);
      shots.push(...liveShots);
    }
    
    // If no real data, use sample data for demonstration
    if (shots.length === 0) {
      const sampleShots = generateSampleShots();
      console.log('Generated sample shots:', sampleShots.length);
      shots.push(...sampleShots);
    }
    
    // Remove duplicates based on timestamp
    const uniqueShots = Array.from(
      new Map(shots.map(shot => [shot.timestamp, shot])).values()
    );
    
    console.log('Total unique shots:', uniqueShots.length);
    return uniqueShots;
  }, [sessionShots, recentShots, liveShots, sessionId]);
  
  // Apply filters
  const filteredShots = useMemo(() => {
    let filtered = [...allShots];
    console.log('Starting with shots:', filtered.length);
    
    // Shot type filter
    if (shotTypeFilter !== 'all') {
      filtered = filtered.filter(shot => shot.shotType === shotTypeFilter);
      console.log('After shot type filter:', filtered.length);
    }
    
    // Quality filter
    if (qualityFilter !== 'all') {
      filtered = filtered.filter(shot => shot.quality === qualityFilter);
      console.log('After quality filter:', filtered.length);
    }
    
    // Time period filter
    if (timePeriod !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch (timePeriod) {
        case 'session':
          // Keep session shots only
          if (sessionId) {
            filtered = filtered.filter(shot => shot.sessionId === sessionId);
            console.log('After session filter:', filtered.length);
          }
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          filtered = filtered.filter(shot => 
            new Date(shot.timestamp) > cutoff
          );
          console.log('After week filter:', filtered.length);
          break;
        case 'month':
          cutoff.setMonth(now.getMonth() - 1);
          filtered = filtered.filter(shot => 
            new Date(shot.timestamp) > cutoff
          );
          console.log('After month filter:', filtered.length);
          break;
      }
    }
    
    console.log('Final filtered shots:', filtered.length);
    return filtered;
  }, [allShots, shotTypeFilter, qualityFilter, timePeriod, sessionId]);
  
  // Calculate comparison data for the best shot
  const shotComparison = useMemo(() => {
    if (filteredShots.length === 0) return null;
    
    // Find the best shot by distance
    const bestShot = filteredShots.reduce((best, shot) => 
      shot.distance > best.distance ? shot : best
    );
    
    return calculateShotComparison(bestShot);
  }, [filteredShots]);
  
  // Extract unique shot types for filter
  const shotTypes = useMemo(() => {
    const types = new Set(allShots.map(shot => shot.shotType));
    return Array.from(types).sort();
  }, [allShots]);
  
  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const total = filteredShots.length;
    if (total === 0) return null;
    
    const avgDistance = filteredShots.reduce((sum, s) => sum + s.distance, 0) / total;
    const boundaries = filteredShots.filter(s => s.distance >= 60).length;
    const boundaryPercentage = (boundaries / total) * 100;
    const excellentShots = filteredShots.filter(s => s.quality === 'excellent').length;
    const excellentPercentage = (excellentShots / total) * 100;
    
    return {
      total,
      avgDistance,
      boundaries,
      boundaryPercentage,
      excellentShots,
      excellentPercentage
    };
  }, [filteredShots]);
  
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export analytics data');
  };
  
  const handleRefresh = () => {
    refetchSession();
  };
  
  // Don't show loading if we have sample data or live shots
  const hasAnyData = allShots.length > 0 || liveShots.length > 0;
  const isLoading = (sessionLoading || recentLoading) && !hasAnyData;
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Debug Info - Remove in production */}
      {import.meta.env.DEV && (
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4" />
          <AlertTitle>Debug Info</AlertTitle>
          <AlertDescription>
            <div className="text-xs space-y-1">
              <div>Total shots: {allShots.length} | Filtered: {filteredShots.length}</div>
              <div>Session shots: {sessionShots?.length || 0} | Recent: {recentShots?.length || 0} | Live: {liveShots.length}</div>
              <div>Filters: Period={timePeriod}, Type={shotTypeFilter}, Quality={qualityFilter}</div>
              <div>Has comparison: {shotComparison ? 'Yes' : 'No'}</div>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Header with filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Cricket Shot Analytics
              </CardTitle>
              <CardDescription>
                Comprehensive analysis of shot performance and patterns
              </CardDescription>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Time Period Filter */}
              <Select value={timePeriod} onValueChange={(value: any) => setTimePeriod(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="session">Session</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Shot Type Filter */}
              <Select value={shotTypeFilter} onValueChange={setShotTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Shot Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Shots</SelectItem>
                  {shotTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Quality Filter */}
              <Select value={qualityFilter} onValueChange={setQualityFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Quality" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quality</SelectItem>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                className="h-10 w-10"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                onClick={handleExport}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {/* Summary Statistics */}
        {summaryStats && (
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Total Shots</p>
                <p className="text-2xl font-bold">{summaryStats.total}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Avg Distance</p>
                <p className="text-2xl font-bold">{summaryStats.avgDistance.toFixed(1)}m</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Boundaries</p>
                <p className="text-2xl font-bold">{summaryStats.boundaryPercentage.toFixed(1)}%</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Excellent</p>
                <p className="text-2xl font-bold">{summaryStats.excellentPercentage.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
      
      {/* Status indicators */}
      {isLive && (
        <Alert className="border-blue-200 bg-blue-50">
          <Activity className="h-4 w-4" />
          <AlertTitle>Live Mode Active</AlertTitle>
          <AlertDescription>
            Analytics are updating in real-time as new shots are recorded.
          </AlertDescription>
        </Alert>
      )}
      
      {!sessionShots && !recentShots && !liveShots.length && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <Info className="h-4 w-4" />
          <AlertTitle>Using Sample Data</AlertTitle>
          <AlertDescription>
            Displaying sample cricket shot data for demonstration. Connect to a database or start a live session to see real data.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Main Analytics Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="field">Field Map</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="analysis">Analysis</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {filteredShots.length > 0 && shotComparison ? (
            <AnalyticsDashboard shotComparison={shotComparison} />
          ) : filteredShots.length > 0 ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Processing Data</AlertTitle>
              <AlertDescription>
                Found {filteredShots.length} shots but unable to generate comparison. Showing sample data.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>No Data Available</AlertTitle>
              <AlertDescription>
                No shots found for the selected filters. Try adjusting the filters or recording some shots.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
        
        {/* Field Map Tab */}
        <TabsContent value="field" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Field Analysis</CardTitle>
                  <CardDescription>
                    Shot distribution across the cricket field
                  </CardDescription>
                </div>
                <Select 
                  value={highlightShotType || 'all'} 
                  onValueChange={(value) => setHighlightShotType(value === 'all' ? undefined : value)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Highlight Shot" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Shots</SelectItem>
                    {shotTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <WagonWheel 
                shots={filteredShots} 
                highlightShotType={highlightShotType}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <HistoricalTrends shots={filteredShots} />
        </TabsContent>
        
        {/* Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Shot Quality Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>Quality Distribution</CardTitle>
                <CardDescription>Breakdown by shot quality</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {['excellent', 'good', 'fair', 'poor'].map(quality => {
                    const count = filteredShots.filter(s => s.quality === quality).length;
                    const percentage = filteredShots.length > 0 ? (count / filteredShots.length * 100) : 0;
                    
                    return (
                      <div key={quality} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge 
                            className={
                              quality === 'excellent' ? 'bg-green-100 text-green-800' :
                              quality === 'good' ? 'bg-blue-100 text-blue-800' :
                              quality === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }
                          >
                            {quality.charAt(0).toUpperCase() + quality.slice(1)}
                          </Badge>
                          <span className="text-sm text-gray-600">{count} shots</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                quality === 'excellent' ? 'bg-green-500' :
                                quality === 'good' ? 'bg-blue-500' :
                                quality === 'fair' ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">{percentage.toFixed(1)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            {/* Top Shot Types */}
            <Card>
              <CardHeader>
                <CardTitle>Top Shot Types</CardTitle>
                <CardDescription>Most frequently played shots</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {shotTypes.slice(0, 5).map(type => {
                    const count = filteredShots.filter(s => s.shotType === type).length;
                    const avgDistance = filteredShots
                      .filter(s => s.shotType === type)
                      .reduce((sum, s) => sum + s.distance, 0) / count || 0;
                    
                    return (
                      <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium">{type}</p>
                          <p className="text-sm text-gray-600">{count} shots</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{avgDistance.toFixed(1)}m</p>
                          <p className="text-sm text-gray-600">avg distance</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Recommendations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Performance Insights
              </CardTitle>
              <CardDescription>
                AI-powered recommendations based on your shot data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredShots.length > 10 && (
                  <>
                    <Alert>
                      <TrendingUp className="h-4 w-4" />
                      <AlertTitle>Strength Identified</AlertTitle>
                      <AlertDescription>
                        Your {shotTypes[0]} is your most consistent shot with {
                          filteredShots.filter(s => s.shotType === shotTypes[0]).length
                        } successful attempts.
                      </AlertDescription>
                    </Alert>
                    
                    {summaryStats && summaryStats.boundaryPercentage < 20 && (
                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertTitle>Area for Improvement</AlertTitle>
                        <AlertDescription>
                          Focus on power generation - only {summaryStats.boundaryPercentage.toFixed(1)}% of your shots reach the boundary.
                          Work on timing and sweet spot contact.
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    {summaryStats && summaryStats.excellentPercentage > 30 && (
                      <Alert className="border-green-200 bg-green-50">
                        <Target className="h-4 w-4 text-green-600" />
                        <AlertTitle>Excellent Performance</AlertTitle>
                        <AlertDescription>
                          Great consistency! {summaryStats.excellentPercentage.toFixed(1)}% of your shots are rated excellent.
                          Keep up the good work!
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnhancedAnalyticsDashboard;